/**
 * i18next initialization. Single flat namespace ('translation') with
 * dot-hierarchy keys (e.g. `sidebar.dashboard`). French is the source language;
 * missing keys fall back to French so a not-yet-translated string never renders
 * as a raw key.
 *
 * Usage in components: `const { t } = useTranslation(); t('sidebar.dashboard')`.
 * The active language is driven by AuthContext once the user profile loads
 * (see `applyUiLanguage`); before that it starts from the browser language.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import fr from './locales/fr.json';
import en from './locales/en.json';
import { resolveInitialUiLanguage, normalizeUiLanguage, type UiLanguage } from './languages';

i18n.use(initReactI18next).init({
  resources: {
    fr: { translation: fr },
    en: { translation: en },
  },
  lng: resolveInitialUiLanguage(),
  fallbackLng: 'fr',
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
});

/** Switch the active UI language (call after persisting the user preference). */
export function applyUiLanguage(lang: string) {
  const code: UiLanguage = normalizeUiLanguage(lang);
  if (i18n.language !== code) i18n.changeLanguage(code);
}

export default i18n;
